import type { Component } from 'solid-js'
import { useParams } from '@solidjs/router'
import { createEffect, createMemo, For, Show } from 'solid-js'
import { useLocationNavigate } from '../ui/utils-ui'

export interface TabConfig {
	panel: string
	label: string
	component: Component<any>
	props?: Record<string, any>
}

interface TabsViaConfigProps {
	config: TabConfig[]
	activePanel?: string
	onTabChange?: (panelName: string) => void
	params?: string[]
	useRouting?: boolean
	routePrefix?: string
	localStorageKey?: string
	normalizePanel?: (panel: string) => string
}

export const TabsViaConfig: Component<TabsViaConfigProps> = (props) => {
	const useRouting = () => props.useRouting !== false
	const routePrefix = () => props.routePrefix || ''
	const storageKey = () => props.localStorageKey || 'tabsViaConfig.activeTab'

	const routeParams = useRouting() ? useParams() : undefined
	const { navigate, location } = useRouting() ? useLocationNavigate() : ({ navigate: undefined, location: undefined } as any)

	const active = createMemo(() => {
		if (!useRouting()) {
			return props.activePanel || props.config[0]?.panel || ''
		}

		const sectionPath = routeParams?.section
		const [section] = sectionPath?.split('/') || [undefined]
		const normalizedSection = props.normalizePanel?.(section || '') || section
		const storedTab = localStorage.getItem(storageKey())
		const normalizedStoredTab = props.normalizePanel?.(storedTab || '') || storedTab
		const defaultPanel = normalizedSection || normalizedStoredTab || props.config[0]?.panel || ''

		return defaultPanel
	})

	const activeParams = createMemo(() => {
		if (!useRouting() || !routeParams?.section) return []
		return routeParams.section.split('/').slice(1)
	})

	const handleTabChange = (panelName: string) => {
		if (useRouting()) {
			localStorage.setItem(storageKey(), panelName)
			const prefix = routePrefix() ? `${routePrefix()}/` : ''
			const path = `/${prefix}${panelName}`
			navigate?.(path, {
				replace: location?.pathname === `/${routePrefix()}` || location?.pathname === '/',
			})
		}

		props.onTabChange?.(panelName)
	}

	const handleTabShow = (evt: CustomEvent) => {
		handleTabChange(evt.detail.name)
	}

	return (
		<div flex='~ col' w-full>
			<wa-tab-group onwa-tab-show={handleTabShow}>
				{/* Render all tabs in nav slot */}
				<For each={props.config}>{tab => (
					<wa-tab slot='nav' panel={tab.panel} active={active() === tab.panel}>
						{tab.label}
					</wa-tab>
				)}
				</For>

				{/* Render all tab panels */}
				<For each={props.config}>{tab => (
					<wa-tab-panel name={tab.panel}>
						<Show when={active() === tab.panel}>
							<tab.component params={activeParams} {...(tab.props || {})} />
						</Show>
					</wa-tab-panel>
				)}
				</For>
			</wa-tab-group>
		</div>
	)
}
